import type {
  EngineIncomingMessage,
  EngineOutgoingMessagePayload,
  EngineOutgoingMessageType,
} from "@bennara/shared";

import {
  createErrorMessage,
  createEngineReadyMessage,
  createTapOnPointMessage,
  createTapOnBodyMessage,
  createDoubleTapOnBodyMessage,
  createTapOutsideMessage,
  createLogMessage,
} from "./messages";
import { validateEngineIncomingMessage } from "./validate";

export type IncomingMessageHandler = (payload: EngineIncomingMessage) => void;

/**
 * Bridge for React Native WebView communication.
 * Handles message parsing and posting.
 */
export class WebViewBridge {
  private handler: IncomingMessageHandler | null = null;

  /**
   * Register handler for incoming messages from React Native.
   * Call this once when ready (e.g. after model loads).
   */
  onMessage(handler: IncomingMessageHandler): void {
    this.handler = handler;
    window.addEventListener("message", (event) => this._handleMessage(event));
  }

  /**
   * Unregister message handler.
   */
  offMessage(): void {
    window.removeEventListener("message", this._handleMessage);
    this.handler = null;
  }

  _postMessage(message: string): void {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(message);
    } else {
      console.log("[WebViewBridge] MESSAGE (not in WebView):", message);
    }
  }

  postLogMessage(message: string): void {
    const payload = createLogMessage(message);
    this._postMessage(payload);
  }

  postErrorMessage(message: string): void {
    const payload = createErrorMessage(message);
    this._postMessage(payload);
  }

  postEngineReady(): void {
    const message = createEngineReadyMessage();
    this._postMessage(message);
  }

  postTapOnPoint({
    pointId,
    location,
  }: EngineOutgoingMessagePayload[EngineOutgoingMessageType.TAP_ON_POINT]): void {
    const message = createTapOnPointMessage({ pointId, location });
    this._postMessage(message);
  }

  postTapOnBody({
    location,
  }: EngineOutgoingMessagePayload[EngineOutgoingMessageType.TAP_ON_BODY]): void {
    const message = createTapOnBodyMessage({ location });
    this._postMessage(message);
  }

  postDoubleTapOnBody(): void {
    this._postMessage(createDoubleTapOnBodyMessage());
  }

  postTapOutside({
    coordinates,
  }: EngineOutgoingMessagePayload[EngineOutgoingMessageType.TAP_OUTSIDE]): void {
    const message = createTapOutsideMessage({ coordinates });
    this._postMessage(message);
  }

  private _handleMessage(event: MessageEvent): void {
    const raw =
      typeof event.data === "string" ? JSON.parse(event.data) : event.data;

    const isArray = Array.isArray(raw);

    if (isArray) {
      raw.forEach((item: unknown) => {
        const message = validateEngineIncomingMessage(item);

        if (this.handler) {
          this.handler(message);
        }
      });
    } else {
      const message = validateEngineIncomingMessage(raw);

      if (this.handler) {
        this.handler(message);
      }
    }
  }
}
